import { useRef, useState } from 'react';
import useGlobalData from '@hooks/useGlobalData';
import useClickOutside from '@hooks/useClickOutside';
import Arrow from '@components/Arrow';
import tw, { styled } from 'twin.macro';
import { navigate } from 'gatsby';

type Props = {
  selectedVersion: string;
};

const VersionsDropdown = ({ selectedVersion }: Props) => {
  const {
    getHomepageByVersion,
    getInternalLink,
    isPreview,
    latestVersion,
    ltsVersion,
    sortedVersions,
  } = useGlobalData();
  const [isOpen, setIsOpen] = useState(false);
  const componentRef = useRef(null);

  useClickOutside(componentRef, () => {
    setIsOpen(false);
  });

  const handleSelectedVersion = (newVersion: string) => {
    setIsOpen(false);
    const newVersionHomepage = getHomepageByVersion(newVersion);
    const newVersionUrl = getInternalLink({ page: newVersionHomepage });

    if (newVersion === latestVersion) {
      navigate(isPreview ? newVersionUrl : '/latest/');
    } else {
      navigate(newVersionUrl);
    }
  };

  // NOTE Beware of mutation
  const versionListItems = [...sortedVersions].reverse().map((version: string) => {
    const isLtsVersion = version === ltsVersion;
    const isLatestVersion = version === latestVersion;

    return (
      <li key={version}>
        <VersionButton
          onClick={() => {
            handleSelectedVersion(version);
          }}
        >
          <VersionLabel isLts={isLtsVersion} isLatest={isLatestVersion}>
            {`${version}${isLtsVersion ? ' LTS' : ''}`}
          </VersionLabel>
        </VersionButton>
      </li>
    );
  });

  return (
    <Wrapper ref={componentRef}>
      <VersionsDropdownButton
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
      >
        Docs
        <SelectedVersion
          isLts={selectedVersion === ltsVersion}
          isLatest={selectedVersion === latestVersion}
        >
          {selectedVersion}
        </SelectedVersion>
        <Arrow isOpen={isOpen} isSmall />
      </VersionsDropdownButton>
      {isOpen && <VersionList>{versionListItems}</VersionList>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${tw`relative`};
`;

const VersionList = styled.ul`
  ${tw`absolute z-10 bg-white border-[1px] border-border rounded-md`}
`;

const VersionsDropdownButton = styled.button`
  ${tw`flex flex-row items-center text-[1.9rem]`};
  ${tw`py-1.5 px-2`};
  ${tw`rounded-md`}
  ${tw`hover:bg-menu-hover`}
`;

const VersionButton = styled.button`
  ${tw`font-bold pl-6 pr-7 py-2`};
  ${tw`text-left`};
  ${tw`w-full`};
  ${tw`hover:bg-grey-light`}
`;

const VersionLabel = styled.span(({ isLts, isLatest }: { isLts: boolean; isLatest: boolean }) => [
  isLatest || isLts ? tw`text-link-light` : tw`text-versionLink`,
]);

const SelectedVersion = styled(VersionLabel)(() => [tw`font-bold text-[1.9rem] px-2`]);

export default VersionsDropdown;
